import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { uploadUrl } from '../../api/client';
import { buildReturnToPath } from '../../navigation/authRedirect';
import styles from './Navbar.module.css';

export function Navbar() {
  const logoSrc = `${process.env.PUBLIC_URL}/favicon.svg`;
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [garagesOpen, setGaragesOpen] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const returnTo = buildReturnToPath(location);

  const handleDropdownPointerEnter = (
    event: React.PointerEvent<HTMLDivElement>,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (event.pointerType === 'mouse') {
      setOpen(true);
    }
  };

  const handleDropdownPointerLeave = (
    event: React.PointerEvent<HTMLDivElement>,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (event.pointerType === 'mouse') {
      setOpen(false);
    }
  };

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setLeaderboardOpen(false);
    setGaragesOpen(false);
    setMemberOpen(false);
  };

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [userMenuOpen]);

  return (
    <header className={styles.header}>
      <Link to="/" className={styles.logo}>
        <span className={styles.logoIcon}>
          <img src={logoSrc} alt="" className={styles.logoIconImg} />
        </span>
        <span>RideSpot</span>
      </Link>

      <nav className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ''}`}>
        <NavLink to="/feed" className={({ isActive }) => (isActive ? styles.active : '')} onClick={closeMobile}>
          Feed
        </NavLink>
        <NavLink to="/search" className={({ isActive }) => (isActive ? styles.active : '')} onClick={closeMobile}>
          Search
        </NavLink>
        <div
          className={styles.navItemWithDropdown}
          onPointerEnter={(event) => handleDropdownPointerEnter(event, setLeaderboardOpen)}
          onPointerLeave={(event) => handleDropdownPointerLeave(event, setLeaderboardOpen)}
        >
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setLeaderboardOpen((open) => !open)}
            aria-expanded={leaderboardOpen}
          >
            <span>Leaderboards</span>
            <span
              className={`${styles.navChevron} ${leaderboardOpen ? styles.navChevronOpen : ''}`}
              aria-hidden="true"
            />
          </button>
          <div className={`${styles.navDropdown} ${leaderboardOpen ? styles.navDropdownOpen : ''}`}>
            <NavLink
              to="/top-contributors"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              All-Time Legends
            </NavLink>
            <NavLink
              to="/most-voted"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              Most voted
            </NavLink>
            <NavLink
              to="/top-spotters-week"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              This Week&apos;s Top Spotters
            </NavLink>
            <NavLink
              to="/favorites"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              Favorites
            </NavLink>
          </div>
        </div>
        <div
          className={styles.navItemWithDropdown}
          onPointerEnter={(event) => handleDropdownPointerEnter(event, setGaragesOpen)}
          onPointerLeave={(event) => handleDropdownPointerLeave(event, setGaragesOpen)}
        >
          <button
            type="button"
            className={styles.navButton}
            onClick={() => setGaragesOpen((open) => !open)}
            aria-expanded={garagesOpen}
          >
            <span>Garages</span>
            <span
              className={`${styles.navChevron} ${garagesOpen ? styles.navChevronOpen : ''}`}
              aria-hidden="true"
            />
          </button>
          <div className={`${styles.navDropdown} ${garagesOpen ? styles.navDropdownOpen : ''}`}>
            <NavLink
              to="/garages/featured"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              Featured
            </NavLink>
            <NavLink
              to="/garages/search"
              className={({ isActive }) => (isActive ? styles.active : '')}
              onClick={closeMobile}
            >
              Search
            </NavLink>
          </div>
        </div>
        <NavLink to="/about" className={({ isActive }) => (isActive ? styles.active : '')} onClick={closeMobile}>
          About
        </NavLink>
        {isAuthenticated && user && (
          <div
            className={styles.navItemWithDropdown}
            onPointerEnter={(event) => handleDropdownPointerEnter(event, setMemberOpen)}
            onPointerLeave={(event) => handleDropdownPointerLeave(event, setMemberOpen)}
          >
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setMemberOpen((open) => !open)}
              aria-expanded={memberOpen}
            >
              <span>Member</span>
              <span
                className={`${styles.navChevron} ${memberOpen ? styles.navChevronOpen : ''}`}
                aria-hidden="true"
              />
            </button>
            <div className={`${styles.navDropdown} ${memberOpen ? styles.navDropdownOpen : ''}`}>
              <NavLink
                to="/garage"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={closeMobile}
              >
                My garage
              </NavLink>
              <NavLink
                to="/my-favorites"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={closeMobile}
              >
                My Favorites
              </NavLink>
              <NavLink
                to="/badges"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={closeMobile}
              >
                Badges
              </NavLink>
              <NavLink
                to="/profile"
                className={({ isActive }) => (isActive ? styles.active : '')}
                onClick={closeMobile}
              >
                Profile
              </NavLink>
            </div>
          </div>
        )}
        <NavLink to="/contact" className={({ isActive }) => (isActive ? styles.active : '')} onClick={closeMobile}>
          Contact
        </NavLink>
        <Link to="/spot" className={styles.addSpotButton} onClick={closeMobile}>
          + Add Spot
        </Link>
      </nav>

      <div className={styles.actions}>
        {isAuthenticated && user ? (
          <div
            className={styles.userMenu}
            ref={userMenuRef}
            onMouseEnter={() => setUserMenuOpen(true)}
          >
            <button
              type="button"
              className={styles.profileIconButton}
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
              aria-label="Profile menu"
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl.startsWith('http') ? user.avatarUrl : uploadUrl(user.avatarUrl)}
                  alt=""
                  className={styles.profileIconImg}
                />
              ) : (
                <span className={styles.profileIconLetter}>{user.displayName.charAt(0)}</span>
              )}
            </button>
            {userMenuOpen && (
              <div className={styles.dropdown}>
                <Link
                  to="/profile"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    setUserMenuOpen(false);
                  }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.authButtons}>
            {location.pathname !== '/login' && (
              <Link to="/login" state={{ returnTo }} className={styles.loginBtn}>
                Sign in
              </Link>
            )}
            <Link to="/create-account" className={styles.createAccountBtn}>
              Create account
            </Link>
          </div>
        )}
      </div>

      <button
        type="button"
        className={styles.burger}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span />
        <span />
        <span />
      </button>
    </header>
  );
}
